import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, Tag as TagIcon, ExternalLink } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    fetch(`${API}/api/blog/articles/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "not_found" : "fetch_failed");
        return r.json();
      })
      .then((j) => { if (alive) setArticle(j); })
      .catch((e) => { if (alive) setError(e.message || "fetch_failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    return () => { document.title = "DDSBillancourt — Auto-école à Boulogne-Billancourt | Pack Permis B 999€"; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8faff]" data-testid="blog-article-loading">
        <Navbar />
        <div className="pt-12 grid place-items-center text-[#5b6577] h-[60vh]">Chargement…</div>
      </div>
    );
  }
  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#f8faff]" data-testid="blog-article-error">
        <Navbar />
        <div className="pt-12 grid place-items-center text-center px-6 h-[60vh]">
          <div>
            <h1 className="font-headings font-black text-3xl text-[#1a2332]">
              {error === "not_found" ? "Article introuvable" : "Erreur de chargement"}
            </h1>
            <p className="text-[#5b6577] mt-2">Cet article n&apos;existe pas ou a été retiré.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 mt-6 bg-[#00a86b] text-white font-headings font-bold rounded-xl px-5 py-2.5">
              <ArrowLeft className="w-4 h-4" /> Retour au blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid="blog-article-page">
      <Seo
        title={`${article.title} | Blog DDSBillancourt`}
        description={article.excerpt || `${article.title} — conseils permis de conduire par l'auto-école DDSBillancourt (Boulogne-Billancourt).`}
        path={`/blog/${article.slug || slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: article.title,
              description: article.excerpt || undefined,
              image: article.cover_image_url || "https://ddsbillancourt.fr/icons/icon-512.png",
              datePublished: article.published_at,
              dateModified: article.updated_at || article.published_at,
              inLanguage: "fr-FR",
              mainEntityOfPage: `https://ddsbillancourt.fr/blog/${article.slug || slug}`,
              author: { "@type": "Organization", name: "DDSBillancourt", url: "https://ddsbillancourt.fr/" },
              publisher: { "@id": "https://ddsbillancourt.fr/#school" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: "https://ddsbillancourt.fr/" },
                { "@type": "ListItem", position: 2, name: "Blog", item: "https://ddsbillancourt.fr/blog" },
                { "@type": "ListItem", position: 3, name: article.title, item: `https://ddsbillancourt.fr/blog/${article.slug || slug}` },
              ],
            },
          ],
        }}
      />
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-12 pb-12 md:pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#5b6577] hover:text-[#00a86b] text-sm font-headings font-semibold mb-8" data-testid="blog-article-back">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </Link>

        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="blog-article-tags">
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/blog?tag=${encodeURIComponent(t)}`}
                className="inline-flex items-center gap-1 text-[11px] font-headings font-bold rounded-full bg-[#00a86b]/10 text-[#00a86b] px-2.5 py-1 uppercase tracking-wide hover:bg-[#00a86b] hover:text-white transition-colors"
              >
                <TagIcon className="w-3 h-3" /> {t}
              </Link>
            ))}
          </div>
        )}

        <h1 className="font-headings font-black text-4xl md:text-5xl tracking-tight leading-tight" data-testid="blog-article-title">
          {article.title}
        </h1>

        <div className="mt-4 flex items-center gap-4 text-sm text-[#5b6577]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" /> Publié le {formatDate(article.published_at)}
          </span>
        </div>

        {article.cover_image_url && (
          <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#00a86b]/5 via-[#f8faff] to-[#FF8C42]/5 shadow-sm overflow-hidden flex items-center justify-center">
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-full max-h-[520px] object-contain"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              data-testid="blog-article-cover"
            />
          </div>
        )}

        <div className="mt-10 prose prose-lg max-w-none prose-headings:font-headings prose-headings:font-black prose-headings:text-[#1a2332] prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-[#1a2332] prose-p:leading-relaxed prose-a:text-[#00a86b] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#1a2332] prose-ul:my-4 prose-li:my-1" data-testid="blog-article-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {article.body_markdown}
          </ReactMarkdown>
        </div>

        {/* CTA footer */}
        <div className="mt-16 rounded-2xl bg-gradient-to-br from-[#00a86b] to-[#0a4d33] text-white p-8 md:p-10">
          <h2 className="font-headings font-black text-2xl md:text-3xl leading-tight">
            Envie de passer ton permis avec DDSBillancourt ?
          </h2>
          <p className="mt-2 text-white/85 text-sm md:text-base">
            Boulogne-Billancourt · Forfait dès 999 € · Simulateur de conduite · E-learning inclus.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-3 text-sm shadow-md transition-all">
              S&apos;inscrire
            </Link>
            <Link to="/blog" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white font-headings font-bold rounded-xl px-5 py-3 text-sm transition-all">
              Lire d&apos;autres articles
            </Link>
          </div>
        </div>

        {article.source_url && (
          <p className="mt-8 text-xs text-[#5b6577]">
            <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#00a86b]">
              Source originale <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        )}
      </article>
      <Footer />
    </div>
  );
}
